import { ContentLabel } from '@/components/ContentLabel'
import type { ReactNode } from 'react'
import cx from 'classnames'
import { Icon } from '@/components/Icon'
import { Checkbox } from '@/components/Checkbox'
import { Button } from '@/components/Button'

import { IconVisual } from '@/components/IconVisual'
import { Dropdown, DropdownItem } from '@/components/Dropdown'
import {
  ContentLayout,
  ContentMain,
  ContentTip,
} from '@/components/ContentLayout'
import { formatRelative } from 'date-fns'
import styles from './Notifications.module.css'
import { byDesign } from '@/infra/design'
import imgNotificationSettingsUrl from '@/assets/misc/img_notification-settings.svg'
import NotificationSettingsIllustration from '@/components/SpotIllustration/NotificationSettingsIllustration'

export interface NotificationsProps {
  header: ReactNode
  corporateMode: boolean
  isSnoozingEnabled: boolean
  /**
   * The loading state, or `NotificationList` once loaded.
   */
  children: ReactNode
}

export function Notifications({
  header,
  corporateMode,
  isSnoozingEnabled,
  children,
}: NotificationsProps) {
  return (
    <>
      {header}
      <ContentLayout accountForLabel>
        <ContentMain>
          <div className={styles.header}>
            <HeaderColumn fill>System Notifications</HeaderColumn>
            {isSnoozingEnabled && <HeaderColumn>Snooze</HeaderColumn>}
            <HeaderColumn>Push</HeaderColumn>
            <HeaderColumn>Email</HeaderColumn>
          </div>
          {children}
        </ContentMain>
        <ContentTip
          corporateMode={corporateMode}
          title="Stay In The Loop"
          illustrationUrl={imgNotificationSettingsUrl}
          illustration={byDesign(undefined, NotificationSettingsIllustration)}
        >
          Easily customise how you want to receive job notifications here. For
          more info on how notifications work, visit our help center.
        </ContentTip>
      </ContentLayout>
    </>
  )
}

export interface NotificationListProps {
  masterItem: ReactNode
  /**
   * One `NotificationPreferenceItem` per notification type.
   */
  items: ReactNode
}

export function NotificationList({ masterItem, items }: NotificationListProps) {
  return (
    <div className={styles.list}>
      <Group>{masterItem}</Group>
      <Divider />
      <Group>{items}</Group>
    </div>
  )
}

interface HeaderColumnProps {
  children?: ReactNode
  fill?: boolean
}

function HeaderColumn({ children, fill }: HeaderColumnProps) {
  return (
    <div className={cx(styles.headerColumn, fill && styles.fill)}>
      <ContentLabel>{children}</ContentLabel>
    </div>
  )
}

interface GroupProps {
  children: ReactNode
}

function Group({ children }: GroupProps) {
  return <div>{children}</div>
}

function Divider() {
  return <div className={styles.divider}></div>
}

export interface NotificationPreferenceItemProps {
  title: string
  description: string
  anySnoozed: boolean
  snoozingOn?: boolean
  snoozeDropdown?: ReactNode
  /**
   * The Push and Email `MediumToggle`s, in that order.
   */
  mediumToggles: ReactNode
}

export function NotificationPreferenceItem({
  title,
  description,
  anySnoozed,
  snoozingOn = false,
  snoozeDropdown,
  mediumToggles,
}: NotificationPreferenceItemProps) {
  return (
    <div className={styles.item}>
      <div className={styles.icon}>
        <IconVisual muted={anySnoozed}>
          <Icon
            size={20}
            name={
              anySnoozed ? 'notifications-thick-muted' : 'notifications-thick'
            }
          />
        </IconVisual>
      </div>
      <div className={styles.content}>
        <p className={styles.title}>{title}</p>
        {description && <p className={styles.description}>{description}</p>}
      </div>

      {snoozingOn && snoozeDropdown}

      {mediumToggles}
    </div>
  )
}

export interface MediumToggleProps {
  checked: boolean
  onToggle: () => unknown
  isPending: boolean
  canToggle: boolean
}

export function MediumToggle({
  checked,
  onToggle,
  isPending,
  canToggle,
}: MediumToggleProps) {
  return (
    <div className={styles.toggleContainer}>
      <Checkbox
        checked={checked}
        onChange={onToggle}
        isPending={isPending}
        isDisabled={isPending || !canToggle}
      />
    </div>
  )
}

export interface SnoozeDropdownProps {
  snoozed: boolean
  snoozedUntil: Date | undefined
  canSnooze: boolean
  isPending: boolean
  onSnooze: (amountHrs: number) => unknown
}

export function SnoozeDropdown({
  snoozed,
  snoozedUntil,
  canSnooze,
  isPending,
  onSnooze,
}: SnoozeDropdownProps) {
  return (
    <div className={styles.snoozeDropdownContainer}>
      <Dropdown
        autoDismiss
        position="bottom-end"
        isDisabled={!canSnooze}
        button={
          <Button
            label={
              snoozed
                ? `Until ${formatRelative(snoozedUntil!, new Date())}`
                : 'Not snoozed'
            }
            className={styles.snoozeDropdownButton}
            variant="secondary"
            borderless
            isDisabled={isPending || !canSnooze}
            isPending={isPending}
            iconRight="dropdown"
            iconSize={24}
          />
        }
      >
        {!snoozed && (
          <>
            <DropdownItem onPress={() => onSnooze(12)}>
              Snooze for 12 Hours
            </DropdownItem>
            <DropdownItem onPress={() => onSnooze(24)}>
              Snooze for 24 Hours
            </DropdownItem>
          </>
        )}
        {snoozed && (
          <DropdownItem onPress={() => onSnooze(0)}>Unsnooze</DropdownItem>
        )}
      </Dropdown>
    </div>
  )
}
